/**
 * Proactive version check — surfaces a "New version available" pill
 * BEFORE the user trips over a stale-chunk ChunkLoadError.
 *
 * Mechanism:
 *   - On boot, we sniff the current document's <script src="main.X.js">
 *     tag to record the hash that's actually running in the browser.
 *   - Periodically (and on every visibilitychange), we fetch the LIVE
 *     /index.html with cache:no-store and parse out its main.*.js hash.
 *   - If the hashes differ, the running bundle is stale and a redeploy
 *     happened since the user opened the tab. We fire `onStale()`.
 *
 * The pill UI (mounted in App.js) calls window.location.reload() when
 * the user clicks — opt-in, not silent like the previous reload-on-
 * error trap was.
 *
 * Why hash comparison vs comparing REACT_APP_VERCEL_GIT_COMMIT_SHA:
 * the hash IS the cache-busting identifier the browser actually uses.
 * Comparing it is the most direct signal that "your loaded JS no
 * longer matches what's deployed." SHA would also work but adds an
 * env-var dependency the build doesn't strictly need.
 */

let _runningHash = null;
let _lastCheckedAt = 0;

// Extract the hash from a <script src="…/main.X.js"></script>-style URL.
// CRA emits filenames like `main.abc123.js` or `static/js/main.abc123.js`.
function extractMainHashFromUrl(url) {
  if (!url) return null;
  const m = String(url).match(/main\.([a-f0-9]+)\.js/);
  return m ? m[1] : null;
}

// Read the hash that's actually running in this tab right now.
export function getRunningMainHash() {
  if (_runningHash) return _runningHash;
  if (typeof document === "undefined") return null;
  // We look at every script tag — the main bundle could be loaded as
  // /static/js/main.X.js (CRA default) or /static/js/main.X.js with a
  // different base path. Match by filename, not full URL.
  const scripts = document.querySelectorAll("script[src]");
  for (const s of scripts) {
    const hash = extractMainHashFromUrl(s.src);
    if (hash) { _runningHash = hash; return hash; }
  }
  return null;
}

// Fetch the live index.html and pull the latest deployed main.*.js hash.
async function fetchLatestMainHash() {
  try {
    // cache:no-store guarantees a fresh trip past any HTTP cache.
    // The query string is belt-and-suspenders for proxies that ignore
    // Cache-Control. /index.html (not /) is explicit so SPAs that
    // serve the same content at every path can't accidentally cache.
    const url = "/index.html?t=" + Date.now();
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) return null;
    const html = await res.text();
    return extractMainHashFromUrl(html);
  } catch {
    return null;
  }
}

/**
 * Single-shot check: returns true if the deployed bundle has a
 * different main hash than the one currently running.
 * Returns false on any error or when both hashes can't be resolved.
 */
export async function isStale() {
  const running = getRunningMainHash();
  if (!running) return false;
  const latest = await fetchLatestMainHash();
  _lastCheckedAt = Date.now();
  if (!latest) return false;
  return latest !== running;
}

export function lastCheckedAt() { return _lastCheckedAt; }
